import { signedWrappedDelta, wrap01 } from "../physics/numbers";
import type { RaceProgressEvent, RaceProgressState, VehicleState } from "./types";

export const RACE_CHECKPOINTS = [0.25, 0.5, 0.75] as const;

const EMPTY_PROGRESS_EVENT: RaceProgressEvent = {
  checkpointPassed: null,
  lapCompleted: false,
  lapTime: null,
  finished: false,
  invalidProgress: false
};

export const createRaceProgressState = (
  initialProgress = 0,
  initialBestLapTime: number | null = null
): RaceProgressState => ({
  completedLaps: 0,
  lap: 1,
  nextCheckpoint: 0,
  currentLapTime: 0,
  lastLapTime: null,
  bestLapTime: initialBestLapTime,
  raceTime: 0,
  lastProgress: wrap01(initialProgress),
  invalidProgressCount: 0,
  finished: false,
  finishTime: null
});

const crossedProgress = (from: number, to: number, target: number): boolean => {
  if (from <= to) {
    return from < target && to >= target;
  }

  return target > from || target <= to;
};

export const advanceRaceProgress = (
  progressState: RaceProgressState,
  trackProgress: number,
  deltaSeconds: number,
  totalLaps: number,
  maximumForwardAdvance = 0.08
): RaceProgressEvent => {
  if (progressState.finished || !Number.isFinite(deltaSeconds) || deltaSeconds <= 0) {
    return { ...EMPTY_PROGRESS_EVENT };
  }

  const nextProgress = wrap01(trackProgress);
  const previousProgress = progressState.lastProgress;
  const forwardAdvance = signedWrappedDelta(previousProgress, nextProgress);
  progressState.lastProgress = nextProgress;
  progressState.currentLapTime += deltaSeconds;
  progressState.raceTime += deltaSeconds;

  if (forwardAdvance <= 0) {
    return { ...EMPTY_PROGRESS_EVENT };
  }

  if (forwardAdvance > maximumForwardAdvance) {
    progressState.invalidProgressCount += 1;
    return { ...EMPTY_PROGRESS_EVENT, invalidProgress: true };
  }

  const expectedCheckpoint = RACE_CHECKPOINTS[progressState.nextCheckpoint];

  if (
    expectedCheckpoint !== undefined &&
    crossedProgress(previousProgress, nextProgress, expectedCheckpoint)
  ) {
    const checkpointPassed = progressState.nextCheckpoint;
    progressState.nextCheckpoint += 1;
    return { ...EMPTY_PROGRESS_EVENT, checkpointPassed };
  }

  const crossedStartLine = previousProgress > nextProgress;

  if (!crossedStartLine) {
    return { ...EMPTY_PROGRESS_EVENT };
  }

  if (progressState.nextCheckpoint !== RACE_CHECKPOINTS.length) {
    progressState.nextCheckpoint = 0;
    progressState.invalidProgressCount += 1;
    return { ...EMPTY_PROGRESS_EVENT, invalidProgress: true };
  }

  const lapTime = progressState.currentLapTime;
  progressState.completedLaps += 1;
  progressState.lastLapTime = lapTime;
  progressState.bestLapTime =
    progressState.bestLapTime === null ? lapTime : Math.min(progressState.bestLapTime, lapTime);
  progressState.currentLapTime = 0;
  progressState.nextCheckpoint = 0;
  progressState.lap = Math.min(progressState.completedLaps + 1, totalLaps);

  if (progressState.completedLaps >= totalLaps) {
    progressState.finished = true;
    progressState.finishTime = progressState.raceTime;
  }

  return {
    checkpointPassed: null,
    lapCompleted: true,
    lapTime,
    finished: progressState.finished,
    invalidProgress: false
  };
};

export const getRaceDistance = (
  progressState: RaceProgressState,
  vehicle: VehicleState,
  totalLaps: number
): number => {
  if (progressState.finished) {
    const finishTime = progressState.finishTime ?? Number.POSITIVE_INFINITY;
    return totalLaps + 1 + 1 / (1 + finishTime);
  }

  return progressState.completedLaps + vehicle.trackProgress;
};

export const getPlayerPosition = (
  player: VehicleState,
  rivals: readonly VehicleState[],
  progressById: ReadonlyMap<string, RaceProgressState>,
  totalLaps: number
): number => {
  const playerProgress = progressById.get(player.id);

  if (playerProgress === undefined) {
    return 1;
  }

  const playerDistance = getRaceDistance(playerProgress, player, totalLaps);
  let position = 1;

  for (const rival of rivals) {
    const rivalProgress = progressById.get(rival.id);

    if (rivalProgress === undefined) {
      continue;
    }

    const rivalDistance = getRaceDistance(rivalProgress, rival, totalLaps);

    if (rivalDistance > playerDistance || (rivalDistance === playerDistance && rival.id < player.id)) {
      position += 1;
    }
  }

  return position;
};
